import React, { useEffect, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@store/index'
import {
  setLoading,
  login,
  logout,
  initializeAuth,
} from '@store/slices/authSlice'
import { MockAuthService } from '@services/mockAuthService'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { UserRole } from './types/index'
import { JobsProvider } from '@contexts/JobsContext'
import LandingPage from './pages/LandingPage'
import DocumentsPage from './pages/DocumentsPage'
import FindTherapists from './pages/PatientPages/FindTherapist'
import TherapistProfilePage from './pages/TherapistProfilePage'
import MessagingPage from './pages/MessagingPage'
import SavedJobsPage from '@pages/SavedJobsPage'
import AdminLogIn from './pages/Admin/AdminLogIn'
import PoCDocumentsPage from './pages/Admin/PoCDocumentsPage'
import PatientLoginPage from './pages/PatientPages/PatientLogin'
import PatientCreateAccountPage from './pages/PatientPages/PatientCreateAccount'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import useAuth from './hooks/useAuth'
import JobsPostingPage from './pages/JobPostCreate'
import CouponManagementPage from './pages/Admin/CouponManagement'
import SigningSuccessPage from './pages/PatientPages/SigningSuccessPage'
import ContractDetailsPage from './pages/ContractDetailsPage'
import SavedTherapist from './pages/PatientPages/SavedTherapist'
import FamilyMemberConsentPage from './pages/FamilyMemberConsentPage'
import PrivacyPage from './pages/PrivacyPage'
import SupportPage from './pages/SupportPage'
import CertificateManager from './pages/Admin/CertificateManager'
import LicenseManagementPage from './pages/Admin/LicenseManagementPage'
import BoardName from './pages/Admin/BoardName'
import BoardSpecialty from './pages/Admin/BoardSpecialty'
import SubSpecialty from './pages/Admin/SubSpecialty'
import LicenseType from './pages/Admin/LicenseType'

// Lazy load pages for code splitting
const LoginPage = React.lazy(() => import('@pages/auth/LoginPage'))
const DashboardPage = React.lazy(() => import('@pages/DashboardPage'))
const SchedulePage = React.lazy(() => import('@pages/SchedulePage'))
const AccountSettings = React.lazy(() => import('@pages/AccountSettings'))
const TherapistSchedulePage = React.lazy(
  () => import('@pages/TherapistSchedulePage')
)
const ContractsPage = React.lazy(() => import('@pages/ContractsPage'))
const JobsPage = React.lazy(() => import('@pages/JobsPage'))
const JobDetailsPage = React.lazy(() => import('@pages/JobDetailsPage'))
const SavedJobDetailsPage = React.lazy(
  () => import('@pages/SavedJobDetailsPage')
)
const PublicJobDetailsPage = React.lazy(
  () => import('@pages/PublicJobDetailsPage')
)
const InvoicesPage = React.lazy(() => import('./pages/invoicesPage'))
const ContractsDemo = React.lazy(() => import('./pages/ContractsDemo'))
const TherapistContractDetailPage = React.lazy(
  () => import('./pages/TherapistContractDetailPage')
)
const EVVLogsPage = React.lazy(() => import('./pages/EVVLogsPage'))
const ClaimsManagementPage = React.lazy(() => import('./pages/ClaimsPage'))
const ClaimTable = React.lazy(() => import('./pages/Claim/Claim'))
const ServicePackageFormPage = React.lazy(
  () => import('./pages/ServicePackageFormPage')
)
const ServicePackageListPage = React.lazy(
  () => import('./pages/ServicePackageListPage')
)
const ProposalsPage = React.lazy(() => import('./pages/ProposalsPage'))
const PatientProposalsPage = React.lazy(
  () => import('./pages/PatientProposalsPage')
)
const OnboardingDemoPage = React.lazy(
  () => import('./pages/OnboardingDemoPage')
)

const TherapistSearchPage = React.lazy(
  () => import('./pages/TherapistSearchPage')
)
const JobListingPage = React.lazy(() => import('./pages/JobListingPage'))
const PillarMatchPage = React.lazy(() => import('./pages/PillarMatchPage'))
const PatientPillarMatchPage = React.lazy(
  () => import('./pages/PatientPages/PatientPillarMatchPage')
)
const TherapistLoginPage = React.lazy(
  () => import('./pages/TherapistLoginPage')
)
const TherapistCreateAccountPage = React.lazy(
  () => import('./pages/TherapistCreateAccountPage')
)
const PaymentSuccessPage = React.lazy(
  () => import('./pages/PaymentSuccessPage')
)
const PaymentCancelPage = React.lazy(() => import('./pages/PaymentCancelPage'))

// Lazy load admin pages for code splitting
const PatientManagementPage = React.lazy(
  () => import('./pages/Admin/PatientManagement')
)
const ProviderManagementPage = React.lazy(
  () => import('./pages/Admin/ProviderManagementPage')
)
const PlanManagement = React.lazy(() => import('./pages/Admin/PlanManagement'))
const SubscriptionManagementPage = React.lazy(
  () => import('./pages/Admin/SubscriptionManagement')
)
const TransactionManagementPage = React.lazy(
  () => import('./pages/Admin/TransactionManagement')
)
const BookingManagementPage = React.lazy(
  () => import('./pages/Admin/BookingManagement')
)
const InvoicesAdminPage = React.lazy(
  () => import('./pages/Admin/InvoicesAdminPage')
)
const ClaimsAdminPage = React.lazy(
  () => import('./pages/Admin/ClaimsAdminPage')
)
const BillingManagement = React.lazy(
  () => import('./pages/Admin/BillingManagement')
)
const AuditLogsPage = React.lazy(() => import('./pages/Admin/AuditLogsPage'))
const PayerListPage = React.lazy(() => import('./pages/Admin/PayerListPage'))
const CPTCodeListPage = React.lazy(
  () => import('./pages/Admin/CPTCodeListPage')
)
const ICDCodeListPage = React.lazy(
  () => import('./pages/Admin/ICDCodeListPage')
)

// Loading fallback component
const PageLoadingFallback: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" color="text.secondary">
      Loading page...
    </Typography>
  </Box>
)

const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated, isLoading } = useAppSelector(
    state => state.auth ?? { isAuthenticated: false, isLoading: false }
  )
  const { role } = useAuth()

  useEffect(() => {
    const initializeAuthAsync = async () => {
      const token = localStorage.getItem('auth-token')

      if (token) {
        try {
          dispatch(setLoading(true))
          const isValid = await MockAuthService.validateToken()

          if (isValid) {
            const user = await MockAuthService.getProfile()
            dispatch(
              login({
                user,
                token,
                refreshToken: '',
              })
            )
          } else {
            dispatch(logout())
          }
        } catch (error) {
          console.error('Auth initialization failed:', error)
          dispatch(logout())
        } finally {
          dispatch(setLoading(false))
        }
      } else {
        dispatch(setLoading(false))
      }
    }

    // Initialize auth from localStorage first
    dispatch(initializeAuth())
    // Then validate with server
    initializeAuthAsync()
  }, [dispatch])

  // Show loading screen during auth initialization
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Initializing application...
        </Typography>
      </Box>
    )
  }

  return (
    <JobsProvider>
      {/* <DocumentsProvider> */}
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/provider-search" element={<TherapistSearchPage />} />
          <Route
            path="/job-listing"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <JobListingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pillar-match"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <PillarMatchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-login"
            element={
              isAuthenticated && role === UserRole.ADMIN ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AdminLogIn />
              )
            }
          />
          <Route
            path="/certificate-login"
            element={
              isAuthenticated && role === UserRole.CERTIFICATE_MANAGER ? (
                <Navigate to="/poc-documents" replace />
              ) : (
                <AdminLogIn />
              )
            }
          />
          <Route
            path="/job-details/:jobId"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <PublicJobDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider-login"
            element={
              isAuthenticated && role === UserRole.THERAPIST ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <TherapistLoginPage />
              )
            }
          />
          <Route
            path="/provider-create-account"
            element={<TherapistCreateAccountPage />}
          />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancel" element={<PaymentCancelPage />} />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage />
              )
            }
          />

          {/* Protected routes with role-based access */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route path="/signing-complete" element={<SigningSuccessPage />} />

          {/* Patient/Client routes */}
          <Route
            path="/patient-login"
            element={
              isAuthenticated && role === UserRole.CLIENT ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <PatientLoginPage />
              )
            }
          />
          <Route
            path="/patient-create-account"
            element={<PatientCreateAccountPage />}
          />

          {/* Family Member Consent Route - Public access via email link */}
          <Route
            path="/patient-consent/:patientId"
            element={<FamilyMemberConsentPage />}
          />

          {/* Public info pages - no login required */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/support" element={<SupportPage />} />

          {/* Password reset routes */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/therapist-reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/patient-reset-password"
            element={<ResetPasswordPage />}
          />

          <Route
            path="/schedule"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <SchedulePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/jobs"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <JobsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/post-new-jobs"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <JobsPostingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/post-new-jobs/:id"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <JobsPostingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-details/:contractId"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <ContractDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <JobDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <ContractsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute
                requiredRole={[UserRole.THERAPIST, UserRole.CLIENT]}
              >
                <InvoicesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/therapy-contracts"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                {/* <TherapistContractsPage /> */}
                <ContractsDemo />
              </ProtectedRoute>
            }
          />

          <Route
            path="/therapy-contracts/:contractId"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <TherapistContractDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/evv-logs"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <EVVLogsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts-demo"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ContractsDemo />
              </ProtectedRoute>
            }
          />

          <Route
            path="/claims"
            element={
              <ProtectedRoute
                requiredRole={[UserRole.THERAPIST, UserRole.CLIENT]}
              >
                <ClaimsManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />

          {/* Therapist/Admin routes */}
          <Route
            path="/service-packages"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ServicePackageListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/service-packages/new"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ServicePackageFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/service-packages/edit/:serviceId"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ServicePackageFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/therapist-schedule"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <TherapistSchedulePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/saved-jobs"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <SavedJobsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/saved-job-details/:jobId"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <SavedJobDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/proposals"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ProposalsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patient-proposals"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <PatientProposalsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patient-pillar-match"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <PatientPillarMatchPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onboarding-demo"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <OnboardingDemoPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute
                requiredRole={[UserRole.CLIENT, UserRole.THERAPIST]}
              >
                <MessagingPage />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="/messages"
            element={
              <ProtectedRoute requiredRole={[UserRole.CLIENT, UserRole.THERAPIST]}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="60vh"
                  gap={2}
                  p={3}
                >
                  <Typography variant="h5">Messages</Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Messages coming soon...
                  </Typography>
                </Box>
              </ProtectedRoute>
            }
          /> */}

          <Route
            path="/claim"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <ClaimTable />
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents-center"
            element={
              <ProtectedRoute requiredRole={UserRole.THERAPIST}>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />

          {/* Common routes */}
          <Route
            path="/account-settings"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />

          {/* Admin routes  */}
          <Route
            path="/Patient"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <PatientManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <ProviderManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plan"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <PlanManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <SubscriptionManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transaction"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <TransactionManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coupons"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <CouponManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <BookingManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-invoices"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <InvoicesAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-claims"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <ClaimsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <BillingManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-payers"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <PayerListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-cpt-codes"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <CPTCodeListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-icd-codes"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <ICDCodeListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificate-manager"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <CertificateManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/board-name"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <BoardName />
              </ProtectedRoute>
            }
          />
          <Route
            path="/board-specialty"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <BoardSpecialty />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sub-specialty"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <SubSpecialty />
              </ProtectedRoute>
            }
          />
          <Route
            path="/license-type"
            element={
              <ProtectedRoute requiredRole={[UserRole.ADMIN]}>
                <LicenseType />
              </ProtectedRoute>
            }
          />
          <Route
            path="/poc-documents"
            element={
              <ProtectedRoute requiredRole={[UserRole.CERTIFICATE_MANAGER]}>
                <PoCDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/license-management"
            element={
              <ProtectedRoute requiredRole={[UserRole.CERTIFICATE_MANAGER]}>
                <LicenseManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<LandingPage />} />

          <Route path="/find-therapist" element={<FindTherapists />} />
          <Route
            path="/find-therapists"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <FindTherapists />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-therapists"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <SavedTherapist />
              </ProtectedRoute>
            }
          />

          <Route
            path="/therapists-details/:therapistId"
            element={
              <ProtectedRoute requiredRole={UserRole.CLIENT}>
                <TherapistProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/therapists-profile-details/:therapistId"
            element={<TherapistProfilePage />}
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="60vh"
                  gap={2}
                  p={3}
                >
                  <Typography variant="h4" color="error">
                    404 - Page Not Found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                  >
                    The page you're looking for doesn't exist.
                  </Typography>
                </Box>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      {/* </DocumentsProvider> */}
    </JobsProvider>
  )
}

export default App
